import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import { useDispatch } from 'react-redux';
import { useEffect, useReducer, useState } from 'react';
import { getAttestationCount } from '@sdk/timeClock/utils/getAttestationCount';

import { clockOutWithAttestations, setHideGreeting } from '@kiosk/timeClock/actions';
import { useAutomatedBreaksContext } from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/AutomatedBreaksProvider/AutomatedBreaksProvider';
import {
  AttestationItemStep,
  Attestation
} from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/AttestationView/attestationView.types';
import { useClockedInViewContext } from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/ClockedInViewProvider/ClockedInViewProvider';

import { Action, updateAttestationStep } from './attestationView.actions';
import { reducer } from './attestationView.reducer';
import { getAttestationsItems } from './attestationView.utils';

export const useAttestationView = () => {
  const dispatchAction = useDispatch();
  const { user } = useClockedInViewContext();
  const { missedBreaks } = useAutomatedBreaksContext();
  const [currentAttestationIndex, setCurrentAttestationIndex] = useState(0);

  const [state, dispatch] = useReducer(reducer, getAttestationsItems(missedBreaks));
  const attestationsCount = getAttestationCount(state);
  const currentAttestationItem = state[currentAttestationIndex];
  const { proceedButton, cancelButton } = useGetButtonsSetup({
    currentAttestationItem,
    setCurrentAttestationIndex,
    dispatch,
    currentAttestationIndex,
    isLastAttestationItem: state.length - 1 === currentAttestationIndex,
    attestations: state
  });

  useEffect(() => {
    dispatchAction(setHideGreeting(true));

    return () => {
      dispatchAction(setHideGreeting(false));
    };
  }, []);

  return {
    user,
    attestationsCount,
    currentAttestationItem,
    proceedButton,
    cancelButton,
    dispatch,
    currentAttestationIndex
  };
};

type GetButtonsSetupProps = {
  currentAttestationItem: Attestation;
  currentAttestationIndex: number;
  setCurrentAttestationIndex: React.Dispatch<React.SetStateAction<number>>;
  dispatch: React.Dispatch<Action>;
  isLastAttestationItem: boolean;
  attestations: Attestation[];
};

export const useGetButtonsSetup = ({
  currentAttestationItem,
  setCurrentAttestationIndex,
  dispatch,
  currentAttestationIndex,
  isLastAttestationItem,
  attestations
}: GetButtonsSetupProps) => {
  const { shift } = useClockedInViewContext();
  const { setAutomatedBreakScreen } = useAutomatedBreaksContext();

  const dispatchAction = useDispatch();

  const onProceedAction = () => {
    if (currentAttestationItem.step === AttestationItemStep.first) {
      if (currentAttestationItem.option === AttestationOption.prevented) {
        return dispatch(
          updateAttestationStep({
            attestIndex: currentAttestationIndex,
            step: AttestationItemStep.second
          })
        );
      }

      if (isLastAttestationItem) {
        return dispatchAction(clockOutWithAttestations(shift.id, attestations));
      }

      return setCurrentAttestationIndex(prev => prev + 1);
    }

    if (currentAttestationItem.step === AttestationItemStep.second && isLastAttestationItem) {
      return dispatchAction(clockOutWithAttestations(shift.id, attestations));
    }

    setCurrentAttestationIndex(prev => prev + 1);
  };

  const onCancelAction = () => {
    if (currentAttestationItem.step === AttestationItemStep.second) {
      dispatch(
        updateAttestationStep({
          attestIndex: currentAttestationIndex,
          step: AttestationItemStep.first
        })
      );
    } else if (currentAttestationIndex === 0) {
      setAutomatedBreakScreen('clockedIn');
    } else {
      setCurrentAttestationIndex(prev => prev - 1);
    }
  };

  const shouldDisabledProceedButton = () => {
    if (currentAttestationItem.step === 1) {
      return !currentAttestationItem.text?.trim();
    }

    return !currentAttestationItem.option;
  };

  return {
    proceedButton: {
      isDisabled: shouldDisabledProceedButton(),
      action: onProceedAction,
      label: currentAttestationItem.step === AttestationItemStep.first ? 'Confirm' : 'Submit',
      themeColor: shouldDisabledProceedButton() ? 'whiteText' : 'successColor',
      buttonTheme: shouldDisabledProceedButton() ? 'disabled' : 'grey'
    },
    cancelButton: {
      disabled: null,
      action: onCancelAction,
      label: 'Back',
      themeColor: null,
      buttonTheme: 'grey'
    }
  };
};
